/**
 * CorrelationsTableRow - Linha da tabela de correlações
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  Checkbox,
  Stack,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlaceIcon from '@mui/icons-material/Place';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import CollectionActions from './CollectionActions';

const CorrelationsTableRow = memo(({
  row,
  devices,
  pois,
  onRefresh,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'approach':
        return t('correlationAlertTypeApproach');
      case 'separation':
        return t('correlationAlertTypeSeparation');
      case 'both':
        return t('correlationAlertTypeBoth');
      default:
        return type;
    }
  };

  // Cor do avatar baseada no tipo
  const avatarColor = row.intrusive
    ? tokens?.semantic?.warning
    : row.targetPoiId > 0
      ? (pois?.[row.targetPoiId]?.color || tokens?.accent?.primary)
      : tokens?.accent?.primary;

  // Status colors
  const enabledColors = row.enabled
    ? { bg: tokens?.semantic?.successBg, color: tokens?.semantic?.success, border: `${tokens?.semantic?.success}40` }
    : { bg: tokens?.semantic?.errorBg, color: tokens?.semantic?.error, border: `${tokens?.semantic?.error}40` };

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            color: avatarColor,
          }}
        >
          {row.intrusive ? (
            <NotificationsActiveIcon fontSize="small" />
          ) : row.targetPoiId > 0 ? (
            <PlaceIcon fontSize="small" />
          ) : (
            <LinkIcon fontSize="small" />
          )}
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Device */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {devices?.[row.deviceId]?.name || row.deviceId}
        </Typography>
      </TableCell>

      {/* Target Device/POI */}
      <TableCell sx={{ py: 1 }}>
        {row.targetPoiId > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlaceIcon fontSize="small" sx={{ color: pois?.[row.targetPoiId]?.color || tokens?.accent?.primary }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {pois?.[row.targetPoiId]?.name || row.targetPoiId}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
            {devices?.[row.targetDeviceId]?.name || (row.targetDeviceId ? row.targetDeviceId : t('sharedAll'))}
          </Typography>
        )}
      </TableCell>

      {/* Radius */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.radiusMeters} m
        </Typography>
      </TableCell>

      {/* Alert Type */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={getAlertTypeLabel(row.alertType)}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            backgroundColor: row.alertType === 'both' ? tokens?.semantic?.warningBg : tokens?.surface?.elevated,
            color: row.alertType === 'both' ? tokens?.semantic?.warning : tokens?.text?.primary,
            border: `1px solid ${row.alertType === 'both' ? `${tokens?.semantic?.warning}40` : tokens?.border?.subtle}`,
          }}
        />
      </TableCell>

      {/* Status */}
      <TableCell sx={{ py: 1 }}>
        <Stack direction="row" spacing={0.5}>
          <Chip
            label={row.enabled ? t('correlationEnabled') : t('sharedDisabled')}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: enabledColors.bg,
              color: enabledColors.color,
              border: `1px solid ${enabledColors.border}`,
            }}
          />
          {row.intrusive && (
            <Chip
              label={t('correlationIntrusive')}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: tokens?.semantic?.warningBg,
                color: tokens?.semantic?.warning,
                border: `1px solid ${tokens?.semantic?.warning}40`,
              }}
            />
          )}
        </Stack>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/correlation"
          endpoint="correlations"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

CorrelationsTableRow.displayName = 'CorrelationsTableRow';

export default CorrelationsTableRow;
